import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { DRAWER_BOUNDS_CLASS } from 'shared/components/ui/DrawerScrim';

/**
 * CapabilityDrawer — the capability detail as a right-side drawer, matching the
 * Capabilities page drawer. Rendered `fixed` at a high z-index so it layers OVER
 * the run-detail popup (which is z-[40]) on the side, exactly like clicking a
 * capability on the Capabilities page.
 *
 * Self-contained: takes one capability object (name, confidence, run_count,
 * satisfaction_score, subject_facet, definition, analysis) plus optional
 * agentName and onRunClick (for the worst-example run links).
 */
function pct(v) {
  const n = Number(v);
  return Number.isFinite(n) ? `${Math.round(n * 100)}%` : '—';
}
function satisfactionColor(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 'var(--text-muted)';
  if (n <= 0.3) return 'var(--feedback-error)';
  if (n >= 0.7) return 'var(--feedback-success)';
  return 'var(--text-primary)';
}

function Section({ title, children }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{title}</p>
      <div className="text-[12px] leading-snug text-[color:var(--text-secondary)]">{children}</div>
    </div>
  );
}

export default function CapabilityDrawer({ capability, agentName, onClose, onRunClick }) {
  // Esc closes. There is no scrim to click away any more, so this is the
  // keyboard exit every other drawer already has.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!capability) return null;
  const a = capability.analysis;
  const kpis = [
    agentName ? { label: 'Agent', value: agentName } : { label: 'Match', value: pct(capability.confidence) },
    { label: 'Runs', value: Number(capability.run_count) || 0 },
    { label: 'Satisfaction', value: pct(capability.satisfaction_score), color: satisfactionColor(capability.satisfaction_score) },
  ];

  return (
    // Beside the table, not over the app: same bounds as every other drawer
    // (see DrawerScrim), one layer up so it sits above an open run popup. No
    // dim — the list behind stays readable and the rail stays usable.
    <div className={`${DRAWER_BOUNDS_CLASS} z-[95]`} role="region" aria-label="Capability detail">
      <section
        className="pointer-events-auto relative flex h-full w-[min(92vw,560px)] flex-col overflow-y-auto border-l border-t border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] shadow-token-lg"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[color:var(--border-default)] bg-surface px-4 py-2.5">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Capability</p>
            <h2 className="truncate text-[14px] font-semibold text-[color:var(--text-primary)]">{capability.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex flex-col gap-4 p-4 text-[12px] leading-snug text-[color:var(--text-secondary)]">
          <div className="grid grid-cols-3 gap-2">
            {kpis.map((k) => (
              <div key={k.label} className="rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{k.label}</p>
                <p className="mt-0.5 truncate text-[13px] font-semibold tabular-nums" style={k.color ? { color: k.color } : { color: 'var(--text-primary)' }}>{k.value}</p>
              </div>
            ))}
          </div>

          {capability.subject_facet ? <Section title="Subject"><p>{capability.subject_facet}</p></Section> : null}
          {capability.definition ? <Section title="What this capability is"><p>{capability.definition}</p></Section> : null}

          {a ? (
            <>
              {a.summary ? <Section title="Summary"><p>{a.summary}</p></Section> : null}
              {a.what_users_want ? <Section title="User goal"><p>{a.what_users_want}</p></Section> : null}
              {Array.isArray(a.friction_points) && a.friction_points.length ? (
                <Section title="Friction points">
                  <ul className="ml-4 list-disc space-y-0.5">{a.friction_points.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </Section>
              ) : null}
              {Array.isArray(a.worst_examples) && a.worst_examples.length ? (
                <Section title="Worst examples">
                  <ul className="space-y-1">
                    {a.worst_examples.map((e, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          onClick={() => onRunClick && onRunClick(e.run_id)}
                          disabled={!onRunClick}
                          className="w-full rounded border border-[color:var(--border-subtle)] bg-surface px-2 py-1.5 text-left transition-colors hover:bg-surface-hover/50 disabled:cursor-default disabled:hover:bg-surface"
                        >
                          <span className="font-mono text-[11px] text-[color:var(--text-muted)]">{String(e.run_id || '').slice(0, 8)}</span>
                          {e.issue ? <span> — {e.issue}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}
              {Array.isArray(a.recommendations) && a.recommendations.length ? (
                <Section title="Recommendations">
                  <ul className="ml-4 list-disc space-y-0.5">{a.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </Section>
              ) : null}
              {a.satisfaction_reason ? (
                <Section title="Why this satisfaction"><p className="text-[color:var(--text-muted)]">{a.satisfaction_reason}</p></Section>
              ) : null}
            </>
          ) : (
            <p className="italic text-[color:var(--text-muted)]">Not yet analysed.</p>
          )}
        </div>
      </section>
    </div>
  );
}
